import { useState, useEffect, useMemo, useRef } from 'react';
import { Download, ExternalLink, Trash2, Play, Square, FileText, Loader2, RotateCcw, TerminalSquare } from 'lucide-react';
import { apiClient } from '../lib/api-client';
import { Badge } from './ui/Badge';
import { Skeleton } from './ui/Skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from './ui/Dialog';
import { Button } from './ui/Button';
import { InlineConfirmation } from './ui/InlineConfirmation';
import { LogSearchBar } from './logs/LogSearchBar';
import { filterLogLinesWithContext, highlightMatches } from './logs/logSearch';
import { TerminalSessionDialog } from './TerminalSessionDialog';

interface Container {
  name: string;
  image: string;
  status: string;
  ports: string;
}

interface ContainerManagementProps {
  serverId: string;
  serverName: string;
  isPR: boolean;
  onClose: () => void;
  onDeleted?: () => void;
}

export function ContainerManagement({ serverId, serverName, isPR, onClose, onDeleted }: ContainerManagementProps) {
  const [containers, setContainers] = useState<Container[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContainer, setSelectedContainer] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [logLines, setLogLines] = useState<string[]>([]);
  const [isLogPinnedToBottom, setIsLogPinnedToBottom] = useState(true);
  const pinnedRef = useRef(true);
  const [isLogDialogOpen, setIsLogDialogOpen] = useState(false);
  const [activeLogContainer, setActiveLogContainer] = useState<string | null>(null);
  const [logSearchQuery, setLogSearchQuery] = useState('');
  const [logCtxBefore, setLogCtxBefore] = useState(0);
  const [logCtxAfter, setLogCtxAfter] = useState(0);
  const [rebuildLog, setRebuildLog] = useState<string | null>(null);
  const [showRebuildLog, setShowRebuildLog] = useState(false);
  const [isLoadingRebuildLog, setIsLoadingRebuildLog] = useState(false);
  const [isRebuildStreaming, setIsRebuildStreaming] = useState(false);
  const [isRebuildPinnedToBottom, setIsRebuildPinnedToBottom] = useState(true);
  const rebuildPinnedRef = useRef(true);
  const rebuildEventSourceRef = useRef<EventSource | null>(null);
  const [rebuildSearchQuery, setRebuildSearchQuery] = useState('');
  const [rebuildCtxBefore, setRebuildCtxBefore] = useState(0);
  const [rebuildCtxAfter, setRebuildCtxAfter] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingRebuild, setIsDownloadingRebuild] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);
  const [sshOpen, setSshOpen] = useState(false);
  const [sshTitle, setSshTitle] = useState('');
  const [sshPath, setSshPath] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const rebuildLogContainerRef = useRef<HTMLDivElement>(null);
  const rebuildLogEndRef = useRef<HTMLDivElement>(null);
  const [logScrollThumb, setLogScrollThumb] = useState<{ topPct: number; heightPct: number; visible: boolean }>({
    topPct: 0,
    heightPct: 100,
    visible: false,
  });
  const isDraggingThumbRef = useRef(false);
  const lastPointerIdRef = useRef<number | null>(null);
  const [rebuildScrollThumb, setRebuildScrollThumb] = useState<{ topPct: number; heightPct: number; visible: boolean }>({
    topPct: 0,
    heightPct: 100,
    visible: false,
  });
  const isDraggingRebuildThumbRef = useRef(false);
  const lastRebuildPointerIdRef = useRef<number | null>(null);

  useEffect(() => {
    fetchContainers();
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (rebuildEventSourceRef.current) {
        rebuildEventSourceRef.current.close();
      }
    };
  }, [serverId]);

  useEffect(() => {
    // Keep the log pane pinned to bottom without scrolling the entire dialog/page.
    // Using `scrollIntoView` here can scroll the whole modal, shifting the left pane.
    if (String(logSearchQuery).trim()) return; // searching: don't auto-follow
    if (!isStreaming) return;
    const el = logContainerRef.current;
    if (!el) return;
    if (!pinnedRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [logLines, isStreaming, isLogPinnedToBottom, logSearchQuery]);

  useEffect(() => {
    // Keep rebuild log pinned to bottom while streaming (matches container logs behavior).
    if (String(rebuildSearchQuery).trim()) return; // searching: don't auto-follow
    if (!isRebuildStreaming) return;
    const el = rebuildLogContainerRef.current;
    if (!el) return;
    if (!rebuildPinnedRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [rebuildLog, isRebuildStreaming, isRebuildPinnedToBottom, rebuildSearchQuery]);

  useEffect(() => {
    // Always-visible scroll indicator for desktop (macOS can hide native scrollbars).
    const el = logContainerRef.current;
    if (!el) return;

    const recompute = () => {
      const scrollHeight = el.scrollHeight;
      const clientHeight = el.clientHeight;
      const scrollTop = el.scrollTop;
      const canScroll = scrollHeight > clientHeight + 1;

      if (!canScroll) {
        setLogScrollThumb({ topPct: 0, heightPct: 100, visible: false });
        pinnedRef.current = true;
        setIsLogPinnedToBottom(true);
        return;
      }

      const pinnedThresholdPx = 8;
      const pinned = scrollTop + clientHeight >= scrollHeight - pinnedThresholdPx;
      pinnedRef.current = pinned;
      setIsLogPinnedToBottom(pinned);

      const heightPct = Math.max(8, Math.min(100, (clientHeight / scrollHeight) * 100));
      const maxScrollTop = scrollHeight - clientHeight;
      const topPct = maxScrollTop > 0 ? (scrollTop / maxScrollTop) * (100 - heightPct) : 0;
      setLogScrollThumb({ topPct, heightPct, visible: true });
    };

    recompute();
    el.addEventListener('scroll', recompute, { passive: true });
    const ro = new ResizeObserver(recompute);
    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', recompute as any);
      ro.disconnect();
    };
  }, [selectedContainer, isStreaming, logLines.length]);

  useEffect(() => {
    // Always-visible scroll indicator for rebuild log too.
    const el = rebuildLogContainerRef.current;
    if (!el) return;

    const recompute = () => {
      const scrollHeight = el.scrollHeight;
      const clientHeight = el.clientHeight;
      const scrollTop = el.scrollTop;
      const canScroll = scrollHeight > clientHeight + 1;

      if (!canScroll) {
        setRebuildScrollThumb({ topPct: 0, heightPct: 100, visible: false });
        rebuildPinnedRef.current = true;
        setIsRebuildPinnedToBottom(true);
        return;
      }

      const pinnedThresholdPx = 8;
      const pinned = scrollTop + clientHeight >= scrollHeight - pinnedThresholdPx;
      rebuildPinnedRef.current = pinned;
      setIsRebuildPinnedToBottom(pinned);

      const heightPct = Math.max(8, Math.min(100, (clientHeight / scrollHeight) * 100));
      const maxScrollTop = scrollHeight - clientHeight;
      const topPct = maxScrollTop > 0 ? (scrollTop / maxScrollTop) * (100 - heightPct) : 0;
      setRebuildScrollThumb({ topPct, heightPct, visible: true });
    };

    recompute();
    el.addEventListener('scroll', recompute, { passive: true });
    const ro = new ResizeObserver(recompute);
    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', recompute as any);
      ro.disconnect();
    };
  }, [showRebuildLog, isRebuildStreaming, rebuildLog?.length]);

  useEffect(() => {
    // If the log dialog closes, stop the live stream so it doesn't keep running in background.
    if (isLogDialogOpen) return;
    if (isStreaming) stopLogStream();
    setActiveLogContainer(null);
    setLogLines([]);
    setLogSearchQuery('');
    setLogCtxBefore(0);
    setLogCtxAfter(0);
  }, [isLogDialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps


  const filteredContainerLogs = useMemo(
    () => filterLogLinesWithContext(logLines, logSearchQuery, logCtxBefore, logCtxAfter),
    [logLines, logSearchQuery, logCtxBefore, logCtxAfter],
  );
  const hasContainerLogSearch = String(logSearchQuery).trim().length > 0;

  const rebuildLines = useMemo(() => String(rebuildLog ?? '').split('\n'), [rebuildLog]);
  const filteredRebuildLogs = useMemo(
    () => filterLogLinesWithContext(rebuildLines, rebuildSearchQuery, rebuildCtxBefore, rebuildCtxAfter),
    [rebuildLines, rebuildSearchQuery, rebuildCtxBefore, rebuildCtxAfter],
  );
  const hasRebuildSearch = String(rebuildSearchQuery).trim().length > 0;

  const fetchContainers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiClient.getServerContainers(serverId);
      setContainers(response.containers || []);
    } catch (err: any) {
      console.error('Failed to fetch containers:', err);
      setError(err.message || 'Failed to load containers');
    } finally {
      setIsLoading(false);
    }
  };

  const startLogStream = (containerName: string) => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    setActiveLogContainer(containerName);
    setLogLines([]);
    setIsStreaming(true);
    setIsLogPinnedToBottom(true);
    setIsLogDialogOpen(true);

    // IMPORTANT:
    // VITE_API_URL is intentionally set to empty string in production to use relative paths.
    // Do NOT use `||` here (it would turn "" into localhost and break prod streaming).
    const apiUrl = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:3000';
    const eventSource = new EventSource(
      `${apiUrl}/api/dev/instances/${encodeURIComponent(serverId)}/containers/${encodeURIComponent(containerName)}/logs/stream`,
      { withCredentials: true }
    );

    eventSource.onmessage = (event) => {
      try {
        // SSE format: "data: {json}\n\n"
        const data = JSON.parse(event.data);
        if (data.type === 'line') {
          setLogLines(prev => {
            const newLines = [...prev, data.line];
            // Keep only last 1000 lines to prevent memory issues
            return newLines.slice(-1000);
          });
        } else if (data.type === 'error') {
          setLogLines(prev => {
            const newLines = [...prev, `[ERROR] ${data.line}`];
            return newLines.slice(-1000);
          });
        } else if (data.type === 'close') {
          eventSource.close();
          setIsStreaming(false);
        }
      } catch (err) {
        console.error('Failed to parse log message:', err, event.data);
      }
    };

    eventSource.onerror = (error) => {
      console.error('EventSource error:', error);
      eventSource.close();
      setIsStreaming(false);
      setError('Log stream connection lost');
    };

    eventSourceRef.current = eventSource;
  };

  const stopLogStream = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsStreaming(false);
  };

  const stopRebuildLogStream = () => {
    if (rebuildEventSourceRef.current) {
      rebuildEventSourceRef.current.close();
      rebuildEventSourceRef.current = null;
    }
    setIsRebuildStreaming(false);
  };

  const downloadLogs = async (containerName: string) => {
    try {
      setIsDownloading(true);
      const logs = await apiClient.getContainerLogs(serverId, containerName);
      const blob = new Blob([logs], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${serverName}-${containerName}-logs.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to download logs:', err);
      alert(`Failed to download logs: ${err.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const restartContainer = async (containerName: string) => {
    try {
      setIsRestarting(true);
      await apiClient.restartContainer(serverId, containerName);
      await fetchContainers();
    } catch (err: any) {
      console.error('Failed to restart container:', err);
      alert(`Failed to restart container: ${err.message}`);
    } finally {
      setIsRestarting(false);
    }
  };

  const openHostSsh = () => {
    setSshTitle(`SSH: ${serverName}`);
    setSshPath(`/api/dev/instances/${encodeURIComponent(serverId)}/ssh`);
    setSshOpen(true);
  };

  const openContainerSsh = (containerName: string) => {
    setSshTitle(`SSH: ${serverName} / ${containerName}`);
    setSshPath(
      `/api/dev/instances/${encodeURIComponent(serverId)}/containers/${encodeURIComponent(containerName)}/ssh`,
    );
    setSshOpen(true);
  };

  const openContainerUrl = async () => {
    try {
      // URL format: https://<serverName>.maxiron.cloud/maximo
      // serverName includes dots (e.g., "dev-manage.breedon")
      const url = `https://${serverName}.maxiron.cloud/maximo`;
      window.open(url, '_blank');
    } catch (err: any) {
      console.error('Failed to open URL:', err);
      alert(`Failed to open URL: ${err.message}`);
    }
  };

  const viewRebuildLog = async () => {
    // Stream rebuild log the same way as container logs (SSE).
    if (rebuildEventSourceRef.current) {
      rebuildEventSourceRef.current.close();
    }

    setShowRebuildLog(true);
    setIsLoadingRebuildLog(true);
    setRebuildLog('');
    setIsRebuildStreaming(true);
    setIsRebuildPinnedToBottom(true);
    rebuildPinnedRef.current = true;

    // Infer tenantCode from serverName: e.g. "dev-manage.breedon" -> "breedon"
    // Use parts[1] to match the extraction logic in DevInstances.tsx
    const parts = String(serverName || '').split('.').map((p) => p.trim()).filter(Boolean);
    const tenantCode = (parts.length >= 2 ? parts[1] : '').toLowerCase();

    const apiUrl = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:3000';
    const url = `${apiUrl}/api/dev/instances/${encodeURIComponent(serverId)}/rebuild-log/stream${tenantCode ? `?tenantCode=${encodeURIComponent(tenantCode)}` : ''}`;
    const es = new EventSource(url, { withCredentials: true });

    es.onopen = () => {
      setIsLoadingRebuildLog(false);
    };

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'line') {
          const line = String(data.line ?? '');
          setRebuildLog((prev) => {
            const nextLines = [...String(prev ?? '').split('\n'), line].slice(-1000);
            return nextLines.join('\n');
          });
        } else if (data.type === 'error') {
          const line = String(data.line ?? '');
          setRebuildLog((prev) => {
            const nextLines = [...String(prev ?? '').split('\n'), `[ERROR] ${line}`].slice(-1000);
            return nextLines.join('\n');
          });
        } else if (data.type === 'close') {
          es.close();
          setIsRebuildStreaming(false);
        }
      } catch (err) {
        console.error('Failed to parse rebuild log message:', err, event.data);
      }
    };

    es.onerror = (error) => {
      console.error('Rebuild EventSource error:', error);
      es.close();
      setIsRebuildStreaming(false);
      setIsLoadingRebuildLog(false);
      setRebuildLog((prev) => {
        const nextLines = [...String(prev ?? '').split('\n'), '[ERROR] Rebuild log stream connection lost'].slice(-1000);
        return nextLines.join('\n');
      });
    };

    rebuildEventSourceRef.current = es;
  };

  const downloadRebuildLog = async () => {
    try {
      setIsDownloadingRebuild(true);
      const logs = await apiClient.getRebuildLog(serverId);
      const blob = new Blob([logs], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${serverName}-rebuild-log.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to download rebuild log:', err);
      alert(`Failed to download rebuild log: ${err.message}`);
    } finally {
      setIsDownloadingRebuild(false);
    }
  };

  const handleDeleteClick = () => {
    if (!isPR) {
      alert('Only PR instances can be deleted');
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await apiClient.deletePRInstance(serverId);
      alert('Server deleted successfully');
      onDeleted?.();
      onClose();
    } catch (err: any) {
      console.error('Failed to delete server:', err);
      alert(`Failed to delete server: ${err.message}`);
    }
  };

  return (
    <>
      <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
        <DialogContent maxWidth="4xl" className="flex flex-col h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Container Management: {serverName}</DialogTitle>
          </DialogHeader>

          {/* Override DialogBody's default overflow-y-auto: we want independent pane scrolls */}
          <DialogBody className="flex-1 p-0 min-h-0 overflow-hidden">
            <div className="flex flex-col md:flex-row flex-1 min-h-0 overflow-hidden">
              {/* Containers list - Full width on mobile, sidebar on desktop */}
              <div className="w-full md:w-64 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-800 overflow-y-auto p-4 max-h-48 md:max-h-none min-h-0">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Containers</h3>
              <button
                onClick={fetchContainers}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Refresh
              </button>
            </div>

            {isLoading ? (
              <div className="space-y-2 py-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : error ? (
              <div className="text-red-600 dark:text-red-400 text-sm">{error}</div>
            ) : containers.length === 0 ? (
              <div className="text-gray-500 dark:text-gray-400 text-sm">No containers found</div>
            ) : (
              <div className="space-y-2">
                {containers.map((container) => (
                  <div
                    key={container.name}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedContainer === container.name
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                    onClick={() => setSelectedContainer(container.name)}
                  >
                    <div className="font-medium text-sm truncate">{container.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                      {container.image}
                    </div>
                    <Badge
                      className={`mt-2 text-xs ${
                        container.status.includes('Up')
                          ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                      }`}
                    >
                      {container.status.split(' ')[0]}
                    </Badge>
                  </div>
                ))}
              </div>
            )}

            {/* Action buttons */}
            <div className="mt-6 space-y-2 border-t border-gray-200 dark:border-gray-800 pt-4">
              <button
                onClick={openHostSsh}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <TerminalSquare className="w-4 h-4" />
                Host SSH
              </button>
              <button
                onClick={viewRebuildLog}
                disabled={isLoadingRebuildLog}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingRebuildLog ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Opening...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4" />
                    {isPR ? 'Build Log' : 'Rebuild Log'}
                  </>
                )}
              </button>
              {isPR && (
                    <>
                      {showDeleteConfirm ? (
                        <InlineConfirmation
                          message={`Delete ${serverName}? This action cannot be undone.`}
                          onConfirm={confirmDelete}
                          onCancel={() => setShowDeleteConfirm(false)}
                          confirmText="Delete"
                          cancelText="Cancel"
                        />
                      ) : (
                <button
                          onClick={handleDeleteClick}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Server
                </button>
                      )}
                    </>
              )}
            </div>
          </div>

              {/* Details area (no live logs here; logs open in a dedicated modal) */}
              <div className="flex-1 flex flex-col min-h-0">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800">
                  <h3 className="font-semibold">Actions</h3>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Select a container from the left, then stream, download logs, restart, or open SSH.
                  </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto p-4">
                  {selectedContainer ? (
                    <div className="space-y-3">
                      <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4">
                        <div className="font-semibold">{selectedContainer}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          Container actions
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                          <Button
                            onClick={() => startLogStream(selectedContainer)}
                            size="sm"
                            className="flex items-center gap-2"
                          >
                            <Play className="w-4 h-4" />
                            Stream logs
                          </Button>

                          <Button
                            onClick={() => downloadLogs(selectedContainer)}
                            disabled={isDownloading}
                            size="sm"
                            variant="outline"
                            className="flex items-center gap-2"
                          >
                            <Download className="w-4 h-4" />
                            {isDownloading ? 'Downloading...' : 'Download logs'}
                          </Button>

                          <Button
                            onClick={() => restartContainer(selectedContainer)}
                            disabled={isRestarting}
                            size="sm"
                            variant="outline"
                            className="flex items-center gap-2"
                          >
                            <RotateCcw className="w-4 h-4" />
                            {isRestarting ? 'Restarting...' : 'Restart'}
                          </Button>

                          <Button
                            onClick={() => openContainerSsh(selectedContainer)}
                            size="sm"
                            variant="outline"
                            className="flex items-center gap-2"
                          >
                            <TerminalSquare className="w-4 h-4" />
                            SSH Session
                          </Button>

                          <Button
                            onClick={() => openContainerUrl()}
                            size="sm"
                            className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Login
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-500 dark:text-gray-400">
                      Select a container to see actions.
                    </div>
                  )}
                </div>
              </div>
        </div>
          </DialogBody>
        </DialogContent>
      </Dialog>

      <TerminalSessionDialog
        open={sshOpen}
        onClose={() => {
          setSshOpen(false);
          setSshPath(null);
        }}
        title={sshTitle}
        description="Interactive session as ubuntu. Close this dialog to disconnect."
        pathWithQuery={sshPath}
      />

      {/* Live log viewer modal (separate dialog, like maximo-dev-tools) */}
      <Dialog open={isLogDialogOpen} onOpenChange={(open) => setIsLogDialogOpen(open)}>
        <DialogContent maxWidth="4xl" className="flex flex-col h-[90vh] overflow-hidden w-full md:w-[80vw] md:max-w-[80vw]">
          <DialogHeader>
            <DialogTitle>
              Container Logs: {activeLogContainer ? `${serverName} / ${activeLogContainer}` : serverName}
            </DialogTitle>
          </DialogHeader>

          <DialogBody className="flex-1 p-0 min-h-0 overflow-hidden">
            <div className="flex flex-col min-h-0 h-full">
              <LogSearchBar
                query={logSearchQuery}
                setQuery={setLogSearchQuery}
                before={logCtxBefore}
                setBefore={setLogCtxBefore}
                after={logCtxAfter}
                setAfter={setLogCtxAfter}
                matchCount={filteredContainerLogs.matchCount}
                totalLines={logLines.length}
              />

              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Live stream (SSE)
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setLogLines([])}
                    size="sm"
                    variant="outline"
                  >
                    Clear
                  </Button>
                  {activeLogContainer && (
                    <Button
                      onClick={() => downloadLogs(activeLogContainer)}
                      disabled={isDownloading}
                      size="sm"
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      {isDownloading ? 'Downloading...' : 'Download'}
                    </Button>
                  )}
                  {isStreaming ? (
                    <Button
                      onClick={stopLogStream}
                      size="sm"
                      className="flex items-center gap-2 bg-red-600 hover:bg-red-700"
                    >
                      <Square className="w-4 h-4" />
                      Stop
                    </Button>
                  ) : (
                    <Button
                      onClick={() => activeLogContainer && startLogStream(activeLogContainer)}
                      disabled={!activeLogContainer}
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <Play className="w-4 h-4" />
                      Stream
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex-1 min-h-0 relative">
                <div
                  ref={logContainerRef}
                  className="h-full overflow-y-scroll p-4 bg-gray-900 text-green-400 font-mono text-sm scrollbar-hidden"
                >
                  {filteredContainerLogs.lines.length === 0 && !isStreaming ? (
                    <div className="text-gray-500">{hasContainerLogSearch ? 'No matches' : 'Click "Stream" to start viewing logs'}</div>
                  ) : (
                    <>
                      {filteredContainerLogs.lines.map((l) => (
                        <div
                          key={`log-${l.idx}`}
                          className={l.isMatch ? 'bg-yellow-500/10 ring-1 ring-yellow-500/30 rounded px-1' : (hasContainerLogSearch ? 'opacity-80' : '')}
                        >
                          <span className="whitespace-pre-wrap break-all">
                            {highlightMatches(l.text, logSearchQuery).map((p, i) =>
                              p.isHit ? (
                                <span key={i} className="bg-yellow-500/40 text-yellow-50 px-0.5 rounded">
                                  {p.text}
                                </span>
                              ) : (
                                <span key={i}>{p.text}</span>
                              ),
                            )}
                          </span>
                        </div>
                      ))}
                      {isStreaming && (
                        <div className="text-yellow-400 animate-pulse">Streaming...</div>
                      )}
                    </>
                  )}
                </div>

                {/* Jump-to-bottom when user scrolls up while streaming */}
                {isStreaming && !hasContainerLogSearch && !isLogPinnedToBottom && (
                  <div className="absolute right-3 bottom-3">
                    <Button
                      size="sm"
                      onClick={() => {
                        const el = logContainerRef.current;
                        if (!el) return;
                        el.scrollTop = el.scrollHeight;
                        pinnedRef.current = true;
                        setIsLogPinnedToBottom(true);
                      }}
                    >
                      Jump to bottom
                    </Button>
                  </div>
                )}

                {/* Thick, always-visible, draggable scrollbar overlay (works even when OS scrollbars are hidden) */}
                <div
                  className="absolute right-2 top-2 bottom-2 w-3"
                  onPointerDown={(e) => {
                    const el = logContainerRef.current;
                    if (!el) return;
                    if (!logScrollThumb.visible) return;
                    isDraggingThumbRef.current = true;
                    lastPointerIdRef.current = e.pointerId;
                    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
                    e.preventDefault();
                  }}
                  onPointerMove={(e) => {
                    if (!isDraggingThumbRef.current) return;
                    const el = logContainerRef.current;
                    if (!el) return;
                    if (!logScrollThumb.visible) return;

                    const track = e.currentTarget as HTMLDivElement;
                    const rect = track.getBoundingClientRect();
                    const y = Math.min(Math.max(e.clientY - rect.top, 0), rect.height);
                    const thumbHeightPx = (logScrollThumb.heightPct / 100) * rect.height;
                    const maxThumbTopPx = Math.max(1, rect.height - thumbHeightPx);
                    const thumbTopPx = Math.min(Math.max(y - thumbHeightPx / 2, 0), maxThumbTopPx);
                    const thumbTopPct = maxThumbTopPx > 0 ? thumbTopPx / maxThumbTopPx : 0;

                    const maxScrollTop = el.scrollHeight - el.clientHeight;
                    el.scrollTop = thumbTopPct * maxScrollTop;
                  }}
                  onPointerUp={(e) => {
                    if (lastPointerIdRef.current !== e.pointerId) return;
                    isDraggingThumbRef.current = false;
                    lastPointerIdRef.current = null;
                  }}
                  onPointerCancel={(e) => {
                    if (lastPointerIdRef.current !== e.pointerId) return;
                    isDraggingThumbRef.current = false;
                    lastPointerIdRef.current = null;
                  }}
                >
                  <div className="absolute inset-0 rounded-full bg-black/25" />
                  <div
                    className="absolute left-0 right-0 rounded-full bg-gray-400/70"
                    style={{ top: `${logScrollThumb.topPct}%`, height: `${logScrollThumb.heightPct}%` }}
                  />
                </div>
              </div>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>

        {/* Rebuild/Build Log Modal */}
      <Dialog open={showRebuildLog} onOpenChange={(open) => {
        if (!open) {
                      stopRebuildLogStream();
                      setShowRebuildLog(false);
                      setRebuildLog(null);
                      setRebuildSearchQuery('');
                      setRebuildCtxBefore(0);
                      setRebuildCtxAfter(0);
                      setIsRebuildPinnedToBottom(true);
                      rebuildPinnedRef.current = true;
                      setRebuildScrollThumb({ topPct: 0, heightPct: 100, visible: false });
        }
      }}>
        {/* Match the live logs dialog sizing so the rebuild log pane is scrollable. */}
        <DialogContent maxWidth="4xl" className="flex flex-col h-[90vh] overflow-hidden w-full md:w-[80vw] md:max-w-[80vw]">
          <DialogHeader>
            <DialogTitle>{isPR ? 'Build Log' : 'Rebuild Log'}: {serverName}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex-1 p-0 min-h-0 overflow-hidden">
            <div className="flex flex-col min-h-0 h-full">
              <LogSearchBar
                query={rebuildSearchQuery}
                setQuery={setRebuildSearchQuery}
                before={rebuildCtxBefore}
                setBefore={setRebuildCtxBefore}
                after={rebuildCtxAfter}
                setAfter={setRebuildCtxAfter}
                matchCount={filteredRebuildLogs.matchCount}
                totalLines={rebuildLines.length}
              />
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Live stream (SSE)
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setRebuildLog('')}
                    size="sm"
                    variant="outline"
                  >
                    Clear
                  </Button>
                  {isRebuildStreaming ? (
                    <Button
                      onClick={stopRebuildLogStream}
                      size="sm"
                      className="flex items-center gap-2 bg-red-600 hover:bg-red-700"
                    >
                      <Square className="w-4 h-4" />
                      Stop
                    </Button>
                  ) : (
                    <Button
                      onClick={viewRebuildLog}
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <Play className="w-4 h-4" />
                      Stream
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex-1 min-h-0 relative">
                <div
                  ref={rebuildLogContainerRef}
                  className="h-full overflow-y-scroll p-4 bg-gray-900 text-green-400 font-mono text-sm whitespace-pre-wrap scrollbar-hidden"
                >
                {isLoadingRebuildLog ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-400">Opening {isPR ? 'build' : 'rebuild'} log...</span>
                  </div>
                ) : rebuildLog ? (
                  <>
                    {filteredRebuildLogs.lines.length === 0 ? (
                      <div className="text-gray-500">{hasRebuildSearch ? 'No matches' : 'No log content'}</div>
                    ) : (
                      filteredRebuildLogs.lines.map((l) => (
                        <div
                          key={`rebuild-${l.idx}`}
                          className={l.isMatch ? 'bg-yellow-500/10 ring-1 ring-yellow-500/30 rounded px-1' : (hasRebuildSearch ? 'opacity-80' : '')}
                        >
                          {highlightMatches(l.text, rebuildSearchQuery).map((p, i) =>
                            p.isHit ? (
                              <span key={i} className="bg-yellow-500/40 text-yellow-50 px-0.5 rounded">
                                {p.text}
                              </span>
                            ) : (
                              <span key={i}>{p.text}</span>
                            ),
                          )}
                        </div>
                      ))
                    )}
                    <div ref={rebuildLogEndRef} />
                  </>
                ) : (
                  `No ${isPR ? 'build' : 'rebuild'} log found`
                )}
              </div>

              {/* Jump-to-bottom when user scrolls up while streaming */}
              {isRebuildStreaming && !hasRebuildSearch && !isRebuildPinnedToBottom && (
                <div className="absolute right-3 bottom-3">
                  <Button
                    size="sm"
                    onClick={() => {
                      const el = rebuildLogContainerRef.current;
                      if (!el) return;
                      el.scrollTop = el.scrollHeight;
                      rebuildPinnedRef.current = true;
                      setIsRebuildPinnedToBottom(true);
                    }}
                  >
                    Jump to bottom
                  </Button>
                </div>
              )}

              {/* Thick, always-visible, draggable scrollbar overlay (same as container logs) */}
              <div
                className="absolute right-2 top-2 bottom-2 w-3"
                onPointerDown={(e) => {
                  const el = rebuildLogContainerRef.current;
                  if (!el) return;
                  if (!rebuildScrollThumb.visible) return;
                  isDraggingRebuildThumbRef.current = true;
                  lastRebuildPointerIdRef.current = e.pointerId;
                  (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
                  e.preventDefault();
                }}
                onPointerMove={(e) => {
                  if (!isDraggingRebuildThumbRef.current) return;
                  const el = rebuildLogContainerRef.current;
                  if (!el) return;
                  if (!rebuildScrollThumb.visible) return;

                  const track = e.currentTarget as HTMLDivElement;
                  const rect = track.getBoundingClientRect();
                  const y = Math.min(Math.max(e.clientY - rect.top, 0), rect.height);
                  const thumbHeightPx = (rebuildScrollThumb.heightPct / 100) * rect.height;
                  const maxThumbTopPx = Math.max(1, rect.height - thumbHeightPx);
                  const thumbTopPx = Math.min(Math.max(y - thumbHeightPx / 2, 0), maxThumbTopPx);
                  const thumbTopPct = maxThumbTopPx > 0 ? thumbTopPx / maxThumbTopPx : 0;

                  const maxScrollTop = el.scrollHeight - el.clientHeight;
                  el.scrollTop = thumbTopPct * maxScrollTop;
                }}
                onPointerUp={(e) => {
                  if (lastRebuildPointerIdRef.current !== e.pointerId) return;
                  isDraggingRebuildThumbRef.current = false;
                  lastRebuildPointerIdRef.current = null;
                }}
                onPointerCancel={(e) => {
                  if (lastRebuildPointerIdRef.current !== e.pointerId) return;
                  isDraggingRebuildThumbRef.current = false;
                  lastRebuildPointerIdRef.current = null;
                }}
              >
                <div className="absolute inset-0 rounded-full bg-black/25" />
                <div
                  className="absolute left-0 right-0 rounded-full bg-gray-400/70"
                  style={{ top: `${rebuildScrollThumb.topPct}%`, height: `${rebuildScrollThumb.heightPct}%` }}
                />
              </div>
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              onClick={downloadRebuildLog}
              disabled={isDownloadingRebuild}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {isDownloadingRebuild ? 'Downloading...' : 'Download Log'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

